'use client'

import { Sparkles } from 'lucide-react'
import { Button } from '@/components/platform/ui'
import { InstrumentLabel, Panel } from '@/components/radar/ui'

interface ReadinessStrategistEntryProps {
  grade: string
  score: number
  /** null when the run produced no gradeable score. */
  hasScore?: boolean
  suggestedQuestions?: string[]
  userTier?: 'free' | 'paid'
  /** Opens the Readiness Strategist chat pre-seeded with this question. */
  onAsk: (question: string) => void
}

const FALLBACK_QUESTIONS = [
  'What should I fix first?',
  'How do I become a cited source?',
  'Why are competitors cited instead?',
]

/**
 * Inline entry into the Readiness Strategist, rendered beside the issues
 * panel so it sits above the fold without dominating it. Tapping a starter
 * opens the existing AdvisorChat pre-seeded; the free-tier message cap is
 * still enforced inside the chat.
 */
export default function ReadinessStrategistEntry({
  grade,
  score,
  hasScore = true,
  suggestedQuestions,
  userTier = 'free',
  onAsk,
}: ReadinessStrategistEntryProps) {
  const letter = (grade || '').charAt(0).toUpperCase()
  const isLow = letter === 'F' || letter === 'D'
  const isHigh = letter === 'A' || letter === 'B'

  const headline = !hasScore
    ? 'Your audit could not be graded. Here is how to read that.'
    : !grade
      ? 'Ask the Readiness Strategist about your AI visibility.'
      : isLow
        ? `You scored ${grade} (${score}/100). Here is the fastest path to fixing it.`
        : isHigh
          ? `${grade} (${score}/100) — strong. Here is how to defend and extend it.`
          : `You scored ${grade} (${score}/100). Here is where to focus to move the needle.`

  const questions = (
    suggestedQuestions && suggestedQuestions.length > 0
      ? suggestedQuestions
      : FALLBACK_QUESTIONS
  ).slice(0, 3)
  const primaryQuestion = questions[0] ?? 'What should I fix first?'

  return (
    <Panel
      gradientRule
      aria-label='Readiness Strategist'
      className='flex h-full flex-col'
    >
      <div className='flex flex-1 flex-col gap-3.5 px-6 py-6 sm:px-7'>
        <InstrumentLabel>
          <Sparkles
            size={14}
            aria-hidden='true'
            className='shrink-0 text-primary'
          />
          Readiness strategist
        </InstrumentLabel>

        <span className='block font-display text-[25px] font-semibold leading-snug tracking-[-0.02em]'>
          {headline}
        </span>
        <span className='block text-sm text-muted-foreground'>
          Trained on leading GEO and AEO methodologies, briefed on your exact
          audit
          {userTier === 'free' ? ' — 5 free messages.' : '.'}
        </span>

        {/* One CTA only. The two suggested-question buttons that used to sit
            beside this competed with it for the same click and pushed the
            card taller than the issues panel next to it. The starter question
            still drives the conversation — it is passed to onAsk below — the
            alternatives just no longer occupy the card. */}
        <div className='mt-auto flex flex-wrap items-center gap-2 pt-1'>
          {/* Solid magenta, matching Run new audit and Fix now. `cta` fills
              with --cta-fill (#d9097a), the calibrated magenta that carries
              white type at 4.9:1 — the raw brand pink is only ~3.6:1. */}
          <Button
            variant='cta'
            size='sm'
            onClick={() => onAsk(primaryQuestion)}
          >
            Ask the strategist
          </Button>
        </div>
      </div>
    </Panel>
  )
}
